import { TourData } from './types';

// Import tour images
import tourDashboard from '../../assets/images/tour/tour_dashboard.png';
import tourCreateDashboard from '../../assets/images/tour/tour_create_dashboard.png';
import tourIntegrationDetails from '../../assets/images/tour/tour_integration_details.png';
import tourWorkspace from '../../assets/images/tour/tour_workspace.png';

/**
 * Dashboard Tour Configuration
 * Guides users through the main dashboard features
 */
export const dashboardTour: TourData = {
  id: 'dashboard-tour',
  title: 'Welcome to Your Dashboard',
  description: 'Let\'s explore the key features of your analytics dashboard',
  requiredPath: '/dashboard',
  steps: [
    {
      target: '[data-tour="sidebar"]',
      title: 'Navigation Sidebar',
      content: 'This is your main navigation area. Access all your dashboards, conversations, and settings from here.',
      placement: 'right',
      spotlightPadding: 8,
    },
    {
      target: '[data-tour="dashboard-list"]',
      title: 'Your Dashboards',
      content: 'View and switch between your analytics dashboards. Each dashboard can contain different charts and data views.',
      placement: 'right',
      image: tourDashboard,
      spotlightPadding: 10,
    },
    {
      target: '[data-tour="create-dashboard"]',
      title: 'Create New Dashboard',
      content: 'Click here to create a new dashboard for organizing your analytics data. You can have multiple dashboards for different projects.',
      placement: 'bottom',
      image: tourCreateDashboard,
      spotlightPadding: 8,
    },
    {
      target: '[data-tour="chat-history"]',
      title: 'Chat History',
      content: 'Access your previous conversations here. All your chat history is organized and easily accessible for reference.',
      placement: 'right',
      spotlightPadding: 8,
    },
    {
      target: '[data-tour="widgets"]',
      title: 'Widgets',
      content: 'This is where all your query widgets live. Visualize your data with charts, tables, and other interactive widgets.',
      placement: 'top',
      spotlightPadding: 10,
    }
  ]
};

/**
 * Workspace Tour Configuration
 * Focuses on workspace management features
 */
export const workspaceTour: TourData = {
  id: 'workspace-tour',
  title: 'Managing Your Workspaces',
  description: 'Learn how to organize your work with workspaces',
  requiredPath: '/integrations',
  steps: [
    {
      target: '[data-tour="workspace-selector"]',
      title: 'Workspace Switcher',
      content: 'Click here to switch between different workspaces or create new ones. Workspaces help organize different projects or teams. Use the dropdown menu to select an existing workspace or create a new one to keep your analytics data organized.',
      placement: 'bottom',
      allowInteraction: true,
      spotlightPadding: 8,
      image: tourWorkspace,
      beforeStep: () => {
        return new Promise<void>((resolve, reject) => {
          console.log('Workspace tour starting, switching to Workspaces tab...');

          // Ensure we're on integrations page and Workspaces tab is active
          const switchToWorkspacesTab = () => {
            // Check current tab
            const currentPath = window.location.pathname;
            console.log('Current path:', currentPath);

            if (currentPath !== '/integrations') {
              // Navigate to integrations first
              window.location.href = '/integrations';
              setTimeout(() => {
                switchToWorkspacesTab();
              }, 1000);
              return;
            }

            // Use custom event to switch tab
            const event = new CustomEvent('tourNavigate', {
              detail: {
                path: '/integrations',
                state: { activeTab: '2' }
              }
            });
            window.dispatchEvent(event);

            // Wait a bit longer for tab switch to complete
            setTimeout(() => {
              startChecking();
            }, 800);
          };

          function startChecking() {
            let attempts = 0;
            const maxAttempts = 50; // 5 seconds total

            const checkElement = () => {
              attempts++;

              // Check if Workspaces tab is active by looking for the workspaces table
              const workspacesTable = document.querySelector('[data-tour="workspaces-table"]');
              const workspaceSelector = document.querySelector('[data-tour="workspace-selector"]') as HTMLElement;

              console.log(`Attempt ${attempts}: Table found=${!!workspacesTable}, Selector found=${!!workspaceSelector}`);

              if (workspaceSelector && workspaceSelector.offsetParent !== null && window.getComputedStyle(workspaceSelector).display !== 'none') {
                console.log('Workspace selector found and visible!');
                resolve();
              } else if (attempts >= maxAttempts) {
                console.error('Workspace selector not found after 5 seconds');
                reject(new Error('Workspace selector not found'));
              } else {
                setTimeout(checkElement, 100);
              }
            };

            checkElement();
          }

          // Start the process
          switchToWorkspacesTab();
        });
      },
    },
    {
      target: '[data-tour="workspaces-table"]',
      title: 'Your Workspaces',
      content: 'This table shows all your workspaces. Each workspace can have its own integrations, dashboards, and team members. You can view details, edit settings, or delete workspaces from here.',
      placement: 'bottom',
      spotlightPadding: 12,
      beforeStep: () => {
        return new Promise<void>((resolve) => {
          const checkTable = () => {
            const table = document.querySelector('[data-tour="workspaces-table"]') as HTMLElement;
            if (table) {
              resolve();
            } else {
              setTimeout(checkTable, 100);
            }
          };
          checkTable();
        });
      },
    },
    {
      target: '[data-tour="create-workspace-button"]',
      title: 'Create New Workspace',
      content: 'Click this button to create a new workspace. You can set up separate workspaces for different projects, clients, or teams to keep your data organized.',
      placement: 'bottom',
      allowInteraction: true,
      spotlightPadding: 8,
      beforeStep: () => {
        return new Promise<void>((resolve) => {
          const checkButton = () => {
            const button = document.querySelector('[data-tour="create-workspace-button"]') as HTMLElement;
            if (button) {
              resolve();
            } else {
              setTimeout(checkButton, 100);
            }
          };
          checkButton();
        });
      },
    }
  ]
};

/**
 * Integrations Tour Configuration
 * Guides users through connecting data sources
 */
export const integrationsTour: TourData = {
  id: 'integrations-tour',
  title: 'Connect Your Data Sources',
  description: 'Learn how to connect and manage your data integrations',
  requiredPath: '/integrations',
  steps: [
    {
      target: '[data-tour="integration-cards"]',
      title: 'Available Integrations',
      content: 'Here are all the data sources you can connect to your analytics dashboard. Each integration provides different types of data.',
      placement: 'bottom',
      spotlightPadding: 12,
      beforeStep: () => {
        return new Promise<void>((resolve) => {
          // Wait for integration cards to be visible
          const checkCards = () => {
            const cards = document.querySelector('[data-tour="integration-cards"]') as HTMLElement;
            if (cards && cards.children.length > 0) {
              resolve();
            } else {
              setTimeout(checkCards, 100);
            }
          };
          checkCards();
        });
      },
    },
    {
      target: '[data-tour="googleanalytics-card"] [data-tour="connect-button"]',
      title: 'Add to Oogwai',
      content: 'Click "Add to Oogwai" on any integration card to start connecting your data source. You\'ll be guided through the authentication process.',
      placement: 'bottom',
      allowInteraction: true,
      spotlightPadding: 8,
      beforeStep: () => {
        return new Promise<void>((resolve) => {
          const checkButton = () => {
            const button = document.querySelector('[data-tour="googleanalytics-card"] [data-tour="connect-button"]') as HTMLElement;
            if (button) {
              resolve();
            } else {
              setTimeout(checkButton, 100);
            }
          };
          checkButton();
        });
      },
    },
    {
      target: '[data-tour="googleanalytics-card"]',
      title: 'Integration Added Successfully',
      content: 'Once added, the button will change to "Added" showing the integration is active. Click on the card anytime to view integration details and sync history.',
      placement: 'bottom',
      allowInteraction: true,
      spotlightPadding: 10,
      // image: tourIntegrationAdded,  // TODO: Add screenshot image here
      beforeStep: () => {
        return new Promise<void>((resolve) => {
          // Check if any integrations are already added
          const checkForAddedIntegrations = () => {
            // Look for any card with an "Added" button
            const allCards = document.querySelectorAll('[data-tour$="-card"]');

            for (let i = 0; i < allCards.length; i++) {
              const card = allCards[i] as HTMLElement;
              const button = card.querySelector('[data-tour="connect-button"]');
              if (button && button.textContent?.includes('Added')) {
                // Found a card with integration already added
                const cardTourId = card.getAttribute('data-tour');
                if (cardTourId) {
                  // Dynamically update this step's target
                  const step = integrationsTour.steps.find(s => s.title === 'Integration Added Successfully');
                  if (step) {
                    step.target = `[data-tour="${cardTourId}"]`;
                  }
                }
                resolve();
                return;
              }
            }

            // No integrations added yet, check for Google Analytics card
            const gaCard = document.querySelector('[data-tour="googleanalytics-card"]');
            if (gaCard) {
              resolve();
            } else {
              setTimeout(checkForAddedIntegrations, 100);
            }
          };

          checkForAddedIntegrations();
        });
      },
    },
    {
      target: '[data-tour="googleanalytics-card"]',
      title: 'View Integration Details',
      content: 'Click on any integration card with "Added" status to view detailed information including sync history, connection status, and management options. You can also skip this step to continue the tour.',
      placement: 'bottom',
      image: tourIntegrationDetails,
      spotlightPadding: 10,
      allowInteraction: true,
      beforeStep: () => {
        return new Promise<void>((resolve) => {
          // Find a card with "Added" status to highlight
          const allCards = document.querySelectorAll('[data-tour$="-card"]');

          for (let i = 0; i < allCards.length; i++) {
            const card = allCards[i] as HTMLElement;
            const button = card.querySelector('[data-tour="connect-button"]');
            if (button && button.textContent?.includes('Added')) {
              // Update target to the added card
              const cardTourId = card.getAttribute('data-tour');
              if (cardTourId) {
                const step = integrationsTour.steps.find(s => s.title === 'View Integration Details');
                if (step) {
                  step.target = `[data-tour="${cardTourId}"]`;
                }
              }
              resolve();
              return;
            }
          }

          // If no added integrations, default to Google Analytics card
          resolve();
        });
      },
    },
    {
      target: '[data-tour="custom-integration-card"]',
      title: 'Custom BigQuery Integration',
      content: 'You can also connect your own custom BigQuery datasets for advanced data analysis. This allows you to integrate any custom data source that you have in BigQuery.',
      placement: 'bottom',
      spotlightPadding: 10,
      beforeStep: () => {
        return new Promise<void>((resolve) => {
          const checkCard = () => {
            const card = document.querySelector('[data-tour="custom-integration-card"]') as HTMLElement;
            if (card) {
              resolve();
            } else {
              setTimeout(checkCard, 100);
            }
          };
          checkCard();
        });
      },
    }
  ]
};

/**
 * Conversation Tour Configuration
 * Introduces users to the AI conversation interface
 */
export const conversationTour: TourData = {
  id: 'conversation-tour',
  title: 'Ask Questions About Your Data',
  description: 'Learn how to use natural language to analyze your data',
  requiredPath: '/conversation',
  steps: [
    {
      target: '[data-tour="data-sources"]',
      title: 'Select Data Sources',
      content: 'Choose which connected data sources you want to query. You can select multiple sources for comprehensive analysis. Make sure you have connected at least one data source from the Integrations page.',
      placement: 'bottom',
      spotlightPadding: 10,
      beforeStep: () => {
        return new Promise<void>((resolve) => {
          // Wait for data sources element to be visible
          const checkElement = () => {
            const element = document.querySelector('[data-tour="data-sources"]') as HTMLElement;
            if (element && element.offsetParent !== null) {
              resolve();
            } else {
              setTimeout(checkElement, 100);
            }
          };
          checkElement();
        });
      },
    },
    {
      target: '[data-tour="chat-input"]',
      title: 'Ask Your Questions',
      content: 'Type your questions here in natural language. For example: "What were my top-selling products last month?" or "How did my ad campaigns perform?" The AI will analyze your data and provide insights with visualizations.',
      placement: 'bottom',
      allowInteraction: true,
      beforeStep: () => {
        return new Promise<void>((resolve) => {
          // Wait for chat input element to be visible
          const checkElement = () => {
            const element = document.querySelector('[data-tour="chat-input"]') as HTMLElement;
            if (element && element.offsetParent !== null) {
              resolve();
            } else {
              setTimeout(checkElement, 100);
            }
          };
          checkElement();
        });
      },
    }
  ]
};

/**
 * Settings Tour Configuration
 * Guides users through account and workspace settings
 */
export const settingsTour: TourData = {
  id: 'settings-tour',
  title: 'Customize Your Experience',
  description: 'Learn about your account and workspace settings',
  requiredPath: '/company-settings',
  steps: [
    {
      target: '[data-tour="profile-settings"]',
      title: 'General Settings',
      content: 'Update your personal information, company details, phone number, timezone, and profile picture. This is where you manage your basic account information.',
      placement: 'right',
      spotlightPadding: 8,
    },
    {
      target: '[data-tour="notifications-settings"]',
      title: 'Notification Preferences',
      content: 'Customize how and when you receive notifications. Manage email alerts, push notifications, and communication preferences.',
      placement: 'right',
      spotlightPadding: 8,
    },
    {
      target: '[data-tour="security-settings"]',
      title: 'Security & Privacy',
      content: 'Manage your password, and security settings to keep your account safe.',
      placement: 'right',
      spotlightPadding: 8,
    },
    {
      target: '[data-tour="billing-settings"]',
      title: 'Plan & Billing',
      content: 'View your current subscription plan, usage statistics, and billing information. Upgrade or downgrade your plan as needed.',
      placement: 'right',
      spotlightPadding: 8,
    },
    {
      target: '[data-tour="feedback-settings"]',
      title: 'Feedback History',
      content: 'View all the feedback you\'ve submitted, track responses, and see the status of your feature requests and bug reports.',
      placement: 'right',
      spotlightPadding: 8,
    },
    {
      target: '[data-tour="referrals-settings"]',
      title: 'Referral Program',
      content: 'Invite friends and colleagues to join Oogwai Analytics. Track your referrals and earn rewards for successful sign-ups.',
      placement: 'right',
      spotlightPadding: 8,
    }
  ]
};

/**
 * First-time User Tour Configuration
 * Comprehensive tour for brand new users
 */
export const firstTimeTour: TourData = {
  id: 'first-time-tour',
  title: 'Welcome to Oogwai Analytics!',
  description: 'Let\'s get you started with the basics',
  steps: [
    {
      target: '[data-tour="main-navigation"]',
      title: 'Main Navigation',
      content: 'Use this navigation to access different areas of your analytics platform.',
      placement: 'bottom',
      spotlightPadding: 8,
      beforeStep: () => {
        return new Promise<void>((resolve, reject) => {
          let attempts = 0;
          const maxAttempts = 50;

          const checkElement = () => {
            attempts++;
            const element = document.querySelector('[data-tour="main-navigation"]') as HTMLElement;

            if (element && element.offsetParent !== null && window.getComputedStyle(element).display !== 'none') {
              console.log('Main navigation found and visible');
              resolve();
            } else if (attempts >= maxAttempts) {
              console.error('Main navigation not found after 5 seconds');
              reject(new Error('Main navigation not found'));
            } else {
              setTimeout(checkElement, 100);
            }
          };

          checkElement();
        });
      },
    },
    {
      target: '[data-tour="workspace-selector"]',
      title: 'Your Workspace',
      content: 'You\'re currently in your workspace. This is where you\'ll organize your dashboards and collaborate with your team.',
      placement: 'bottom',
      spotlightPadding: 8,
      beforeStep: () => {
        return new Promise<void>((resolve, reject) => {
          let attempts = 0;
          const maxAttempts = 50;

          const checkElement = () => {
            attempts++;
            const element = document.querySelector('[data-tour="workspace-selector"]') as HTMLElement;

            if (element && element.offsetParent !== null && window.getComputedStyle(element).display !== 'none') {
              console.log('Workspace selector found and visible');
              resolve();
            } else if (attempts >= maxAttempts) {
              console.error('Workspace selector not found after 5 seconds');
              reject(new Error('Workspace selector not found'));
            } else {
              setTimeout(checkElement, 100);
            }
          };

          checkElement();
        });
      },
    },
    {
      target: '[data-tour="sidebar"]',
      title: 'Quick Access',
      content: 'Your sidebar provides quick access to dashboards, conversations, and recently viewed content.',
      placement: 'right',
      spotlightPadding: 10,
      beforeStep: () => {
        return new Promise<void>((resolve, reject) => {
          let attempts = 0;
          const maxAttempts = 50;

          const checkElement = () => {
            attempts++;
            const element = document.querySelector('[data-tour="sidebar"]') as HTMLElement;

            if (element) {
              const styles = window.getComputedStyle(element);
              // For fixed elements, check if display is not 'none' and element exists in DOM
              const isVisible = styles.display !== 'none' && element.getBoundingClientRect().width > 0;

              if (isVisible) {
                console.log('Sidebar found and visible');
                resolve();
              } else if (attempts >= maxAttempts) {
                console.error('Sidebar not visible after 5 seconds');
                reject(new Error('Sidebar not visible'));
              } else {
                setTimeout(checkElement, 100);
              }
            } else if (attempts >= maxAttempts) {
              console.error('Sidebar element not found in DOM');
              reject(new Error('Sidebar not found'));
            } else {
              setTimeout(checkElement, 100);
            }
          };

          checkElement();
        });
      },
    },
  ]
};

/**
 * Tour registry - maps tour IDs to tour configurations
 */
export const tourRegistry = {
  dashboard: dashboardTour,
  workspace: workspaceTour,
  integrations: integrationsTour,
  conversation: conversationTour,
  settings: settingsTour,
  'first-time': firstTimeTour,
} as const;

/**
 * Get tour by ID
 */
export const getTour = (tourId: keyof typeof tourRegistry): TourData | null => {
  return tourRegistry[tourId] || null;
};

/**
 * Get all available tour IDs
 */
export const getAllTourIds = (): Array<keyof typeof tourRegistry> => {
  return Object.keys(tourRegistry) as Array<keyof typeof tourRegistry>;
};

/**
 * Tour order for multi-page onboarding flow
 * Uses registry keys (not tour IDs)
 */
export const tourOrder = [
  'first-time',
  'dashboard',
  'integrations',
  'conversation',
  'workspace',
  'settings'
] as const;

/**
 * Map tour IDs to registry keys
 */
const tourIdToRegistryKey: Record<string, keyof typeof tourRegistry> = {
  'first-time-tour': 'first-time',
  'dashboard-tour': 'dashboard',
  'integrations-tour': 'integrations',
  'conversation-tour': 'conversation',
  'workspace-tour': 'workspace',
  'settings-tour': 'settings',
};

/**
 * Get the next tour in the sequence
 */
export const getNextTour = (currentTourId: string): TourData | null => {
  // Map the tour ID to registry key
  const registryKey = tourIdToRegistryKey[currentTourId];
  if (!registryKey) {
    console.warn(`Unknown tour ID: ${currentTourId}`);
    return null;
  }

  const currentIndex = tourOrder.indexOf(registryKey);
  if (currentIndex === -1 || currentIndex === tourOrder.length - 1) {
    return null;
  }

  return getTour(tourOrder[currentIndex + 1]);
};

export default tourRegistry;